import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, withAdmin } from "@/lib/api";
import { Trash2, Save, LogOut, FlaskConical } from "lucide-react";

const LS_KEY = "marta.admin.token";

export default function Admin() {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [weeks, setWeeks] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");

  async function login(e) {
    e?.preventDefault();
    setError("");
    try {
      const r = await api.post("/admin/login", { password });
      setToken(r.data.token);
      localStorage.setItem(LS_KEY, r.data.token);
    } catch {
      setError("Wrong password");
    }
  }

  async function refresh() {
    if (!token) return;
    try {
      const [w, r, o] = await Promise.all([
        api.get("/admin/weeks", withAdmin(token)),
        api.get("/admin/rewards", withAdmin(token)),
        api.get("/admin/overview", withAdmin(token)),
      ]);
      setWeeks(w.data);
      setRewards(r.data);
      setOverview(o.data);
    } catch {
      localStorage.removeItem(LS_KEY);
      setToken("");
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [token]);

  function logout() {
    localStorage.removeItem(LS_KEY);
    setToken("");
    nav("/");
  }

  if (!token) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-5">
        <form onSubmit={login} className="surface w-full max-w-sm p-6">
          <h1 className="text-[26px] text-[hsl(var(--ink))]">Parent zone</h1>
          <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">Enter the admin password.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
            className="mt-4 w-full px-4 py-3 rounded-2xl bg-[hsl(var(--surface-2))] font-medium text-[hsl(var(--ink))] focus:outline-none shadow-inner"
          />
          {error && <p data-testid="admin-login-error" className="text-sm font-semibold text-[hsl(var(--destructive))] mt-2">{error}</p>}
          <button data-testid="admin-login-submit" className="btn btn-primary w-full mt-5">Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="pb-24 pt-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-[32px] text-[hsl(var(--ink))] leading-tight">Parent zone</h1>
        <div className="flex items-center gap-2">
          <Link to="/admin/test" className="btn btn-amber !py-2 !px-4 text-sm" data-testid="admin-test-mode-btn">
            <FlaskConical className="w-4 h-4"/> Test mode
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="btn btn-ghost !py-2 !px-4 text-sm">
            <LogOut className="w-4 h-4"/> Logout
          </button>
        </div>
      </div>

      {overview && (
        <section className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-overview">
          <Mini label="XP" v={overview.xp}/>
          <Mini label="Level" v={overview.level.level}/>
          <Mini label="Streak" v={overview.streak}/>
          <Mini label="Perfect weeks" v={overview.perfect_weeks}/>
          <Mini label="English done" v={overview.english_completed}/>
          <Mini label="Drum practices" v={overview.drum_sessions}/>
          <Mini label="Drum minutes" v={overview.drum_minutes}/>
          <Mini label="Achievements" v={overview.achievements.length}/>
        </section>
      )}

      <section className="mt-8">
        <h2 className="text-[20px] text-[hsl(var(--ink))] mb-3">Weeks</h2>
        <div className="space-y-4">
          {weeks.map((w) => (
            <WeekEditor key={w.id} week={w} token={token} onSaved={refresh}/>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-[20px] text-[hsl(var(--ink))] mb-3">Mystery rewards</h2>
        <RewardList rewards={rewards} token={token} onChange={refresh}/>
      </section>
    </div>
  );
}

function Mini({ label, v }) {
  return (
    <div className="surface-soft p-3">
      <p className="micro-label">{label}</p>
      <p className="text-xl font-semibold text-[hsl(var(--ink))]">{v}</p>
    </div>
  );
}

function WeekEditor({ week, token, onSaved }) {
  const [status, setStatus] = useState(week.status);
  const [reqE, setReqE] = useState(week.required_english_tasks);
  const [reqD, setReqD] = useState(week.required_drum_sessions);
  const [minMin, setMinMin] = useState(week.minimum_drum_minutes);
  const [quests, setQuests] = useState(week.english_quests.map((q) => ({ ...q })));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    await api.patch(`/admin/weeks/${week.id}`, {
      status,
      required_english_tasks: Number(reqE),
      required_drum_sessions: Number(reqD),
      minimum_drum_minutes: Number(minMin),
    }, withAdmin(token));
    for (const q of quests) {
      await api.patch(`/admin/english-quests/${q.id}`, {
        title: q.title,
        instructions: q.instructions || "",
        content: q.content || "",
        attachment_url: q.attachment_url || null,
        external_url: q.external_url || null,
        tasks: (q.tasks || []).map((t) => ({
          id: t.id,
          question: t.question,
          options: t.options,
          correct_index: Number(t.correct_index),
        })),
      }, withAdmin(token));
    }
    setSaving(false);
    onSaved();
  }

  return (
    <div className="surface p-5" data-testid={`week-editor-${week.start_date}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="micro-label">Week of</p>
          <p className="text-[20px] font-semibold text-[hsl(var(--ink))] mt-0.5">
            {new Date(week.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </p>
          <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">
            {week.english_done}/{week.required_english_tasks} English · {week.drum_done}/{week.required_drum_sessions} Drums
            {week.perfect_week_completed_at && <span className="ml-2 chip chip-violet">Perfect</span>}
          </p>
        </div>
        <select
          value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-xl bg-[hsl(var(--surface-2))] px-3 py-2 font-semibold text-sm"
          data-testid={`week-status-${week.start_date}`}
        >
          <option value="normal">Normal</option>
          <option value="vacation">Vacation</option>
          <option value="sick">Sick</option>
          <option value="excused">Excused</option>
        </select>
      </div>

      {/* English setup */}
      <div className="mt-4">
        <p className="micro-label">English</p>
        <div className="mt-1">
          <Num label="Quests required" v={reqE} set={setReqE} testid={`req-english-${week.start_date}`}/>
        </div>
      </div>

      {/* Drum setup — explicit per request */}
      <div className="mt-4">
        <p className="micro-label">Drum practice</p>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <Num label="Sessions required this week" v={reqD} set={setReqD} testid={`req-drum-sessions-${week.start_date}`}/>
          <Num label="Minutes required per session" v={minMin} set={setMinMin} testid={`req-drum-minutes-${week.start_date}`}/>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {quests.map((q, i) => (
          <QuestEditor
            key={q.id}
            quest={q}
            weekKey={week.start_date}
            index={i}
            onChange={(next) => updateQuest(setQuests, quests, i, next)}
          />
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid={`save-week-${week.start_date}`} className="btn btn-primary mt-4">
        <Save className="w-4 h-4"/> {saving ? "Saving…" : "Save week"}
      </button>
    </div>
  );
}

function updateQ(setter, quests, i, k, v) {
  const next = quests.slice();
  next[i] = { ...next[i], [k]: v };
  setter(next);
}

function updateQuest(setter, quests, i, nextQuest) {
  const copy = quests.slice();
  copy[i] = nextQuest;
  setter(copy);
}

function QuestEditor({ quest, weekKey, index, onChange }) {
  const tasks = quest.tasks || [];
  function set(k, v) { onChange({ ...quest, [k]: v }); }
  function updateTask(ti, patch) {
    const next = tasks.slice();
    next[ti] = { ...next[ti], ...patch };
    set("tasks", next);
  }
  function addTask() {
    const t = { id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      question: "", options: ["", ""], correct_index: 0 };
    set("tasks", [...tasks, t]);
  }
  function removeTask(ti) {
    const next = tasks.slice();
    next.splice(ti, 1);
    set("tasks", next);
  }
  function addOption(ti) {
    const t = tasks[ti];
    if (t.options.length >= 5) return;
    updateTask(ti, { options: [...t.options, ""] });
  }
  function removeOption(ti, oi) {
    const t = tasks[ti];
    if (t.options.length <= 2) return;
    const options = t.options.slice();
    options.splice(oi, 1);
    const correct_index = Math.min(t.correct_index, options.length - 1);
    updateTask(ti, { options, correct_index });
  }

  return (
    <div className="surface-tint p-4">
      <p className="micro-label mb-1">Quest {index + 1}</p>
      <input className="w-full px-3 py-2 rounded-xl bg-white font-semibold" placeholder="Title"
        value={quest.title} onChange={(e) => set("title", e.target.value)}
        data-testid={`quest-title-${weekKey}-${index}`}/>
      <textarea className="mt-2 w-full px-3 py-2 rounded-xl bg-white" rows={2} placeholder="Instructions"
        value={quest.instructions || ""} onChange={(e) => set("instructions", e.target.value)}/>
      <textarea className="mt-2 w-full px-3 py-2 rounded-xl bg-white" rows={4}
        placeholder="Reading passage (longer text, optional)"
        value={quest.content || ""} onChange={(e) => set("content", e.target.value)}/>
      <input className="mt-2 w-full px-3 py-2 rounded-xl bg-white" placeholder="External URL (optional)"
        value={quest.external_url || ""} onChange={(e) => set("external_url", e.target.value)}/>

      {/* Tasks */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <p className="micro-label">Tasks (multiple choice)</p>
          <button
            type="button" onClick={addTask}
            data-testid={`add-task-${weekKey}-${index}`}
            className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--violet-deep))]"
          >+ Add task</button>
        </div>
        {tasks.length === 0 && (
          <p className="mt-2 text-xs text-[hsl(var(--ink-soft))]">
            No tasks yet. Marta can just mark the quest as finished after reading.
          </p>
        )}
        <div className="mt-2 space-y-3">
          {tasks.map((t, ti) => (
            <div key={t.id || ti} className="rounded-2xl bg-white p-3 shadow-[0_6px_14px_-10px_hsla(258,20%,20%,0.25)]">
              <div className="flex items-start justify-between gap-2">
                <p className="micro-label">Q{ti + 1}</p>
                <button
                  type="button" onClick={() => removeTask(ti)}
                  data-testid={`remove-task-${weekKey}-${index}-${ti}`}
                  className="text-xs font-bold text-[hsl(var(--destructive))]"
                >Remove</button>
              </div>
              <textarea
                className="mt-1 w-full px-3 py-2 rounded-xl bg-[hsl(var(--surface-2))] font-semibold"
                rows={2}
                placeholder="Question"
                value={t.question}
                onChange={(e) => updateTask(ti, { question: e.target.value })}
                data-testid={`task-question-${weekKey}-${index}-${ti}`}
              />
              <div className="mt-2 space-y-1.5">
                {t.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correct-${weekKey}-${index}-${ti}`}
                      checked={t.correct_index === oi}
                      onChange={() => updateTask(ti, { correct_index: oi })}
                      data-testid={`task-correct-${weekKey}-${index}-${ti}-${oi}`}
                      className="w-4 h-4 accent-[hsl(var(--violet))]"
                      title="Mark as correct answer"
                    />
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 rounded-xl bg-[hsl(var(--surface-2))] font-medium"
                      placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                      value={opt}
                      onChange={(e) => {
                        const options = t.options.slice();
                        options[oi] = e.target.value;
                        updateTask(ti, { options });
                      }}
                      data-testid={`task-option-${weekKey}-${index}-${ti}-${oi}`}
                    />
                    {t.options.length > 2 && (
                      <button
                        type="button" onClick={() => removeOption(ti, oi)}
                        className="text-xs font-bold text-[hsl(var(--ink-mute))] px-2"
                        title="Remove option"
                      >✕</button>
                    )}
                  </div>
                ))}
                {t.options.length < 5 && (
                  <button
                    type="button" onClick={() => addOption(ti)}
                    data-testid={`add-option-${weekKey}-${index}-${ti}`}
                    className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--violet-deep))]"
                  >+ Add option</button>
                )}
              </div>
              <p className="mt-1 text-[11px] text-[hsl(var(--ink-mute))]">Tick the radio next to the correct answer.</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Num({ label, v, set, testid }) {
  return (
    <label className="block">
      <span className="micro-label">{label}</span>
      <input type="number" value={v} data-testid={testid}
        onChange={(e)=>set(e.target.value)}
        className="w-full mt-0.5 px-3 py-2 rounded-xl bg-white font-semibold"/>
    </label>
  );
}

function RewardList({ rewards, token, onChange }) {
  const [name, setName] = useState("");
  const [teaser, setTeaser] = useState("Mystery reward");
  const [type, setType] = useState("perfect_weeks");
  const [amount, setAmount] = useState(4);

  async function create() {
    if (!name.trim()) return;
    await api.post("/admin/rewards", { internal_name: name, child_teaser: teaser, requirement_type: type, requirement_amount: Number(amount) }, withAdmin(token));
    setName(""); setAmount(4);
    onChange();
  }
  async function del(id) {
    await api.delete(`/admin/rewards/${id}`, withAdmin(token));
    onChange();
  }

  return (
    <div className="space-y-3">
      {rewards.map((r) => (
        <div key={r.id} className="surface-soft p-4 flex items-center justify-between gap-3" data-testid={`admin-reward-${r.id}`}>
          <div>
            <p className="font-semibold text-[hsl(var(--ink))]">{r.internal_name}</p>
            <p className="text-sm text-[hsl(var(--ink-soft))]">Teaser: &ldquo;{r.child_teaser}&rdquo;</p>
            <p className="text-xs text-[hsl(var(--ink-mute))] mt-0.5">
              {r.requirement_amount} {r.requirement_type.replace("_", " ")} · <span className={r.status === "unlocked" ? "text-[hsl(var(--violet-deep))]" : ""}>{r.status}</span>
            </p>
          </div>
          <button onClick={() => del(r.id)} className="p-2 rounded-xl text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4"/></button>
        </div>
      ))}
      <div className="surface p-4">
        <p className="micro-label">New reward</p>
        <input className="mt-2 w-full px-3 py-2 rounded-xl bg-[hsl(var(--surface-2))] font-semibold" placeholder="Internal name" value={name} onChange={(e)=>setName(e.target.value)} data-testid="reward-name-input"/>
        <input className="mt-2 w-full px-3 py-2 rounded-xl bg-[hsl(var(--surface-2))] font-semibold" placeholder="Child teaser" value={teaser} onChange={(e)=>setTeaser(e.target.value)}/>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <select value={type} onChange={(e)=>setType(e.target.value)} className="px-3 py-2 rounded-xl bg-[hsl(var(--surface-2))] font-semibold">
            <option value="perfect_weeks">Perfect Weeks</option>
            <option value="english_quests">English Quests</option>
            <option value="drum_sessions">Drum Practices</option>
            <option value="xp">XP</option>
          </select>
          <input type="number" value={amount} onChange={(e)=>setAmount(e.target.value)} className="px-3 py-2 rounded-xl bg-[hsl(var(--surface-2))] font-semibold"/>
        </div>
        <button onClick={create} className="btn btn-primary mt-3" data-testid="create-reward-btn">Create reward</button>
      </div>
    </div>
  );
}
